import React from "react";

/**
 * Formats a full swara string into proper Indian classical notation:
 * - "komal X"       → X with underline (flat)
 * - "Ma (Shuddha)"  → Ma (natural)
 * - "Ma (Tivra)"    → Mᵗ (sharp/tivra marker)
 * - taar notes      → superscript plus (e.g. Sa⁺, Re⁺)
 * - others          → plain text
 */
export function formatSwara(swara: string, className?: string): React.ReactElement {
    if (!swara) return <span className={className}></span>;
    const isTaar = swara.endsWith("'");
    const baseSwara = isTaar ? swara.slice(0, -1) : swara;
    const octaveMark = isTaar
        ? <sup style={{ fontSize: "0.65em", verticalAlign: "super", lineHeight: 0 }}>+</sup>
        : null;

    if (baseSwara.startsWith("komal ")) {
        const name = baseSwara.slice(6); // strips "komal "
        return (
            <span className={className} style={{ textDecorationLine: "underline", textUnderlineOffset: "3px" }}>
                {name}{octaveMark}
            </span>
        );
    }

    if (baseSwara === "Ma (Tivra)") {
        return (
            <span className={className}>
                M
                <sup style={{ fontSize: "0.65em", verticalAlign: "super", lineHeight: 0 }}>t</sup>
                {octaveMark}
            </span>
        );
    }

    if (baseSwara === "Ma (Shuddha)") {
        return <span className={className}>Ma{octaveMark}</span>;
    }

    return <span className={className}>{baseSwara}{octaveMark}</span>;
}

/**
 * Returns the abbreviated short label for a swara (plain text, no JSX).
 * Useful for slider labels where full JSX may not be convenient.
 */
export function swaraShortLabel(swara: string): string {
    const isTaar = swara.endsWith("'");
    const baseSwara = isTaar ? swara.slice(0, -1) : swara;

    let label = baseSwara;
    if (baseSwara.startsWith("komal ")) label = baseSwara.slice(6); // "Re", "Ga" etc (rendered with underline separately)
    else if (baseSwara === "Ma (Tivra)") label = "Mᵗ";
    else if (baseSwara === "Ma (Shuddha)") label = "Ma";

    return isTaar ? `${label}⁺` : label;
}

/** True if this swara is komal (flat) and should be underlined */
export function isKomal(swara: string): boolean {
    const baseSwara = swara.endsWith("'") ? swara.slice(0, -1) : swara;
    return baseSwara.startsWith("komal ");
}
